import { notify } from '@kyvg/vue3-notification';

/**
 * Central toast API for the admin client.
 *
 * Every toast in the app should go through this composable rather than
 * calling `notify()` directly, so the group, type, and duration are
 * consistent everywhere and copy lives next to the action that raised it.
 *
 * Usage inside a component:
 *
 *   const toast = useToast();
 *   toast.success('User Updated', 'The user has been successfully updated.');
 *   toast.fromError(e, 'Could not update user');
 */

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
    /** Milliseconds before the toast auto-dismisses. `-1` keeps it until closed. */
    duration?: number;
}

export interface ToastHandle {
    id: number;
    close: () => void;
}

export const TOAST_GROUP = 'app-notifications';

/** Default lifetimes per type. Errors linger longer because they need reading. */
export const TOAST_DURATION: Record<ToastType, number> = {
    success: 4000,
    info: 5000,
    warning: 7000,
    error: 8000
};

let nextId = 1;

const show = (type: ToastType, title: string, text = '', options: ToastOptions = {}): ToastHandle => {
    const id = nextId++;
    notify({
        id,
        group: TOAST_GROUP,
        type,
        title,
        text,
        duration: options.duration ?? TOAST_DURATION[type]
    });
    return { id, close: () => notify.close(id) };
};

const STATUS_MESSAGES: Record<number, string> = {
    400: 'The request was invalid. Please check the details and try again.',
    401: 'Your session has expired. Please sign in again.',
    403: "You don't have permission to do that.",
    404: 'The item you were looking for could not be found.',
    409: 'This conflicts with something that already exists.',
    422: 'Some of the details provided were not accepted.',
    429: 'Too many requests. Please wait a moment and try again.',
    500: 'Something went wrong on the server. Please try again.',
    502: 'The server is temporarily unavailable. Please try again shortly.',
    503: 'The server is temporarily unavailable. Please try again shortly.',
    504: 'The server took too long to respond. Please try again.'
};

const pickString = (...candidates: unknown[]): string | undefined => {
    for (const c of candidates) {
        if (typeof c === 'string' && c.trim()) return c.trim();
    }
    return undefined;
};

/**
 * Turn whatever was thrown (an axios error, an Error, a string, or an
 * API payload) into a sentence a user can act on.
 */
export const messageFromError = (error: unknown, fallback = 'Something went wrong. Please try again.'): string => {
    if (!error) return fallback;
    if (typeof error === 'string') return error;

    const e = error as any;
    const response = e.response;
    const data = response?.data;

    // Prefer a message the API deliberately sent back.
    const apiMessage = pickString(
        data?.error?.message,
        data?.error,
        data?.message,
        data?.detail,
        data?.title,
        typeof data === 'string' ? data : undefined
    );
    if (apiMessage) return apiMessage;

    const status: number | undefined = response?.status;
    if (status && STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];
    if (status && status >= 500) return STATUS_MESSAGES[500];

    // Axios network failures have no response at all.
    if (e.isAxiosError && !response) {
        return "We couldn't reach the server. Check your connection and try again.";
    }
    if (e.code === 'ECONNABORTED') {
        return STATUS_MESSAGES[504];
    }

    return pickString(e.message) ?? fallback;
};

export const toast = {
    success: (title: string, text?: string, options?: ToastOptions) => show('success', title, text, options),
    error: (title: string, text?: string, options?: ToastOptions) => show('error', title, text, options),
    warning: (title: string, text?: string, options?: ToastOptions) => show('warning', title, text, options),
    info: (title: string, text?: string, options?: ToastOptions) => show('info', title, text, options),

    /**
     * Show an error toast for a caught exception. The title says what the
     * user was trying to do; the body is derived from the error itself.
     */
    fromError: (error: unknown, title = 'Something went wrong', options?: ToastOptions & { fallback?: string }) =>
        show('error', title, messageFromError(error, options?.fallback), options),

    /** Programmatically dismiss a single toast. */
    close: (handle: ToastHandle | number) => notify.close(typeof handle === 'number' ? handle : handle.id),

    /** Clear everything currently on screen. */
    dismissAll: () => notify({ group: TOAST_GROUP, clean: true })
};

export type Toast = typeof toast;

export const useToast = (): Toast => toast;

export default useToast;
